import ApiClient from './ApiClient';

/**
 * Task 103 -- client half of the planner job runner. Owner, verbatim: "it needs to be set up so
 * that the page can be navigated from and the process is still running on the server, if the page
 * is come back to, the user can see the current status", widened to "can this apply to all analyse
 * requests, not just breaking up a shipment?".
 *
 * Every analyze used to be a 30-90 second `await ApiClient.post(...)` held open by the browser, so
 * the work existed only as long as the tab did. Now the server owns the work and this module owns
 * three jobs on the client side:
 *
 *   1. KICK OFF and get a job id back immediately.
 *   2. POLL that job until it reaches a terminal phase.
 *   3. RE-ATTACH to a job already in flight when a page mounts (`findActiveJob`) -- the part that
 *      actually delivers "come back and see the current status", because a returning page has no
 *      job id to hand back.
 *
 * The poll interval is deliberately short (`JOB_POLL_INTERVAL_MS`). A quick analyze must still
 * resolve in a poll or two behind the same spinner the operator has always seen: the visible change
 * is that the work survives navigation, NOT that the UI grew a queue.
 *
 * Nothing here can confirm a shipment -- these helpers only ever reach the analyze/job endpoints.
 */

export const JOB_POLL_INTERVAL_MS = 1000;

/**
 * Task: cross-page completion toast. Owner, verbatim: "once analyze shipment button is pressed for
 * ltl it should hand off to the server - can we use toast to pop a notification up if the user is
 * on a different page?" The analyze already hands off to the server (this module); what was missing
 * was a way for a page the operator has since navigated AWAY from to still learn the job finished.
 *
 * `plannerJobWatch` in localStorage is that bridge: `registerPlannerJobWatch` is called from
 * `runAnalyzeJob` below (the one seam every analyze kickoff already goes through), so no page has to
 * opt in. `PlannerJobToaster` (mounted once at app level) polls the watched ids and pops a toast
 * when one goes terminal, regardless of which page is on screen.
 */
const PLANNER_JOB_WATCH_KEY = 'plannerJobWatch';
const PLANNER_JOB_WATCH_MAX = 10;

/** The current watch list, oldest first. Corrupt/garbage storage reads back as empty, never throws. */
export function readPlannerJobWatch() {
    try {
        const raw = window.localStorage.getItem(PLANNER_JOB_WATCH_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((e) => e && e.jobId != null);
    } catch (err) {
        return [];
    }
}

/** Adds/refreshes a watch entry for a job. Capped at the newest 10; storage failures are swallowed
 * -- a lost watch entry only costs a missed toast, never a broken analyze. */
export function registerPlannerJobWatch({ jobId, label } = {}) {
    if (jobId == null) return;
    try {
        const rest = readPlannerJobWatch().filter((e) => e.jobId !== jobId);
        const next = [...rest, { jobId, label: label || null, startedAt: Date.now() }]
            .slice(-PLANNER_JOB_WATCH_MAX);
        window.localStorage.setItem(PLANNER_JOB_WATCH_KEY, JSON.stringify(next));
    } catch (err) {
        // best-effort
    }
}

/** Removes one watched job (its toast has been shown), or every entry when `jobId` is omitted. */
export function clearPlannerJobWatch(jobId) {
    try {
        if (jobId == null) {
            window.localStorage.removeItem(PLANNER_JOB_WATCH_KEY);
            return;
        }
        const remaining = readPlannerJobWatch().filter((e) => e.jobId !== jobId);
        window.localStorage.setItem(PLANNER_JOB_WATCH_KEY, JSON.stringify(remaining));
    } catch (err) {
        // best-effort
    }
}

/** Scope vocabulary, mirroring PlannerJobService's constants. */
export const SCOPE_DRAFT = 'DRAFT';
export const SCOPE_SPLIT_GROUP = 'SPLIT_GROUP';
export const SCOPE_PLANNER = 'PLANNER';
export const SCOPE_KEY_PO_MODE = 'po-mode';

/** Phases a job can report. QUEUED/RUNNING are the "keep polling" pair. */
export const PHASE_NONE = 'NONE';
export const PHASE_QUEUED = 'QUEUED';
export const PHASE_RUNNING = 'RUNNING';
export const PHASE_DONE = 'DONE';
export const PHASE_FAILED = 'FAILED';
export const PHASE_INTERRUPTED = 'INTERRUPTED';

/** True while the server still has work to do on this job. */
export function jobIsActive(job) {
    if (!job) return false;
    if (typeof job.active === 'boolean') return job.active;
    return job.phase === PHASE_QUEUED || job.phase === PHASE_RUNNING;
}

/**
 * The spinner line for a job, in the vocabulary the operator already reads. Multi-step jobs count
 * shipments ("Analyzing 2 of 3…"); single-step jobs keep the old wording so a normal analyze looks
 * exactly as it always has.
 */
export function jobProgressLabel(job, { singleStepLabel } = {}) {
    if (!job) return '';
    const total = job.total || 0;
    const done = job.done || 0;
    // WAITING is not WORKING. 2026-08-21: a job sat QUEUED behind another for 178 seconds while
    // this said "Analyzing 1 of 3", and the owner reasonably concluded it was hung. A queued job
    // must say it is queued -- and say how many are ahead of it, so "be patient" and "something is
    // broken" stop looking identical.
    if (job.phase === PHASE_QUEUED) {
        const ahead = job.queuePosition;
        if (typeof ahead === 'number' && ahead > 0) {
            return `Queued — ${ahead} analyze${ahead === 1 ? '' : 's'} ahead of it…`;
        }
        return 'Queued — starting next…';
    }
    if (total > 1) return `Analyzing ${Math.min(done + 1, total)} of ${total}…`;
    return singleStepLabel
        || 'Scoring placement options against regional demand (this may take 30-60 seconds)…';
}

/** The error text a terminal-but-unsuccessful job should surface. Null for a healthy DONE. */
export function jobFailureMessage(job) {
    if (!job) return null;
    if (job.phase === PHASE_FAILED) {
        return job.error
            || (job.steps || []).map(s => s.analysisError).find(Boolean)
            || 'analyze failed';
    }
    if (job.phase === PHASE_INTERRUPTED) {
        return job.error
            || 'The server restarted while this analyze was running. Nothing was confirmed — re-run it.';
    }
    return null;
}

/** One job by id. */
export async function fetchJob(jobId) {
    const res = await ApiClient.get(`/api/shipment-planner/jobs/${encodeURIComponent(jobId)}`);
    return res?.data || null;
}

/**
 * Re-attach lookup: the newest job for a workspace, or null. Scope-based rather than id-based on
 * purpose -- a page that was reloaded knows which draft/split group it is showing, but has lost
 * every id it was holding in memory.
 */
export async function findLatestJob(scopeType, scopeKey) {
    if (!scopeType || !scopeKey) return null;
    try {
        const res = await ApiClient.get('/api/shipment-planner/jobs', {
            params: { scopeType, scopeKey, limit: 1 },
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        return rows[0] || null;
    } catch (err) {
        // Discovery is best-effort: a page that cannot ask must still render normally.
        return null;
    }
}

/** Re-attach lookup narrowed to a job that is still running. */
export async function findActiveJob(scopeType, scopeKey) {
    const job = await findLatestJob(scopeType, scopeKey);
    return jobIsActive(job) ? job : null;
}

/**
 * The analysis a finished job produced, fetched from the run it wrote. Deliberately NOT duplicated
 * into the job's own result: `GET /runs/{id}` already returns the exact AnalysisResponse shape the
 * synchronous analyze returned, and it is the same endpoint the `?run=` deep link has always used.
 * One source of truth for "what did this analyze find".
 */
export async function fetchRunAnalysis(runId) {
    if (runId == null) return null;
    const res = await ApiClient.get(`/api/shipment-planner/runs/${encodeURIComponent(runId)}`);
    return res?.data || null;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls a job to a terminal phase, reporting every intermediate reading through `onProgress`.
 *
 * `isCancelled` lets a component that unmounted stop polling WITHOUT stopping the job -- that
 * distinction is the whole feature: the browser walks away, the server does not.
 *
 * A transient poll failure is tolerated (the job is still running server-side; a dropped status
 * read is not a failed analyze). Only a terminal job phase ends the loop.
 */
export async function waitForJob(jobId, {
    onProgress,
    intervalMs = JOB_POLL_INTERVAL_MS,
    isCancelled = () => false,
} = {}) {
    let consecutiveErrors = 0;
    for (;;) {
        if (isCancelled()) return null;
        let job = null;
        try {
            job = await fetchJob(jobId);
            consecutiveErrors = 0;
        } catch (err) {
            consecutiveErrors += 1;
            if (consecutiveErrors >= 10) throw err;
        }
        if (job) {
            if (onProgress) onProgress(job);
            if (!jobIsActive(job)) return job;
        }
        await sleep(intervalMs);
    }
}

/**
 * Kick off an analyze job, tolerating the "already running" case.
 *
 * A 409 means this workspace already has a job in flight (a second tab, a double click, or a
 * kickoff whose response was lost). Re-attaching to that job is strictly better than surfacing an
 * error or, worse, creating a second set of Amazon plans for the same units.
 */
export async function startJob(url, body, { params } = {}) {
    try {
        const res = await ApiClient.post(url, body === undefined ? {} : body, { params });
        return res?.data || null;
    } catch (err) {
        if (err?.response?.status === 409 && params?.scopeType && params?.scopeKey) {
            const existing = await findActiveJob(params.scopeType, params.scopeKey);
            if (existing) return existing;
        }
        throw err;
    }
}

/**
 * The whole round trip for a single-step analyze: kick off, poll, and hand back the analysis the
 * run recorded -- the same object shape the old blocking POST resolved with, so call sites keep
 * their existing result handling.
 *
 * @throws Error carrying the server's own message on a FAILED/INTERRUPTED job.
 */
export async function runAnalyzeJob({ url, body, scopeType, scopeKey, label, onProgress, isCancelled }) {
    // scopeType rides along for startJob's 409 re-attach lookup; the kickoff endpoints read only
    // scopeKey/label and Spring ignores the extra query param.
    const params = { scopeType, scopeKey };
    if (label) params.label = label;
    const started = await startJob(url, body, { params });
    if (!started?.jobId) throw new Error('the server did not start the analyze');
    // Registered here, not per-page: this is the one seam every analyze kickoff already goes
    // through, so PlannerJobToaster can notify from any page without page-level code changes.
    registerPlannerJobWatch({ jobId: started.jobId, label });
    if (onProgress) onProgress(started);
    // Already terminal: the 409 re-attach can hand back a job that finished between the refusal
    // and the lookup. Polling a job that is already done would only add a wasted round trip.
    if (!jobIsActive(started)) return resolveFinishedJob(started);
    const finished = await waitForJob(started.jobId, { onProgress, isCancelled });
    if (!finished) return null; // cancelled by unmount -- the job keeps running on the server
    return resolveFinishedJob(finished);
}

/** A terminal job -> its analysis, or a throw carrying the server's reason. */
export async function resolveFinishedJob(job) {
    const failure = jobFailureMessage(job);
    if (failure) {
        const err = new Error(failure);
        // Task 105: an unconfirmed-carton refusal is a structured ASK. The synchronous endpoint
        // returns it in the 400 body; a job has no HTTP response to carry it, so the runner puts
        // it in the job result and it is re-attached here. Without this every ASIN-mode analyze
        // -- which is all of them -- degrades the ask back into a bare error string.
        if (job?.result?.cartonDataGap) err.cartonDataGap = job.result.cartonDataGap;
        throw err;
    }
    const runId = job?.runId ?? (job?.steps || []).map(s => s.runId).find(v => v != null);
    if (runId == null) throw new Error('the analyze finished without producing a run');
    const analysis = await fetchRunAnalysis(runId);
    if (!analysis) throw new Error(`run ${runId} could not be loaded`);
    return analysis;
}

/**
 * Split-set status, in the shape the set panel reads. Always resolves (a set with no job answers
 * `phase: 'NONE'`), because "nothing has been started" is a status, not an error.
 */
export async function fetchSplitSetStatus(groupId) {
    if (!groupId) return null;
    try {
        const res = await ApiClient.get(
            `/api/shipment-planner/split-sets/${encodeURIComponent(groupId)}/status`);
        return res?.data || null;
    } catch (err) {
        return null;
    }
}

/**
 * Server step states -> the per-draft outcome shape the split-set panel already renders
 * (`splitMemberStatus`). Keeps the panel's presentation untouched; it just gets its facts from the
 * server now instead of from a browser-local ledger that a reload could not repopulate.
 */
export function splitOutcomesFromJob(job, existing = {}) {
    const out = { ...(existing || {}) };
    (job?.members || job?.steps || []).forEach((step) => {
        const draftId = step?.refKey;
        if (!draftId) return;
        const prior = out[draftId] || {};
        // A confirm is recorded locally and has no server step; never let a step state erase it.
        if (prior.confirmed) return;
        if (step.state === 'ANALYZED') {
            out[draftId] = { ...prior, runId: step.runId ?? prior.runId ?? null,
                decision: step.decision ?? prior.decision ?? null, error: null };
        } else if (step.state === 'FAILED') {
            // The run id is kept when there is one: a member Amazon never answered for still has a
            // real inbound plan that has to stay reachable and cancellable. The VERDICT is what is
            // withdrawn -- the same rule the assessment page applies to `analysisError`.
            out[draftId] = { ...prior, runId: step.runId ?? prior.runId ?? null, decision: null,
                error: step.analysisError || 'analyze failed' };
        } else if (step.state === 'INTERRUPTED') {
            out[draftId] = { ...prior, error: step.analysisError || 'interrupted by a server restart' };
        }
    });
    return out;
}

/** The draft id of the step the server is working on right now, or null. Drives the panel's
 * per-row "Analyzing…" marker without the page having to own the loop. */
export function runningDraftId(job) {
    const step = (job?.members || job?.steps || []).find(s => s?.state === 'RUNNING');
    return step?.refKey || null;
}

/**
 * `{done, total, queued, queuePosition}` for the panel's progress button, or null when nothing is
 * in flight. `queued` is what stops a waiting job from claiming to be analyzing -- see
 * jobProgressLabel for the incident that made this necessary.
 */
export function runAllProgress(job) {
    if (!jobIsActive(job)) return null;
    return {
        done: job.done || 0,
        total: job.total || 0,
        queued: job.phase === PHASE_QUEUED,
        queuePosition: job.queuePosition,
    };
}

/**
 * Records which placement the operator just selected for a run. Owner 2026-08-21: "the FC column in
 * the shipment runs page should show the last selected option - not nessearly your recommended
 * option" — which needed the selection to exist somewhere other than this browser tab.
 *
 * FIRE AND FORGET. The caller never awaits it and never sees a failure: this is a preference, not a
 * commitment. If the write is lost the run falls back to showing the recommendation, which is
 * exactly what it did before — a persistence hiccup must not interrupt what the operator was doing
 * or leave their selection stuck.
 */
export function rememberSelectedPlacement(runId, placementOptionId) {
    if (runId == null || !placementOptionId) return;
    Promise.resolve(
        ApiClient.post(`/api/shipment-planner/runs/${encodeURIComponent(runId)}/selected-placement`,
            { placementOptionId })
    ).catch(() => { /* preference only — never surfaced */ });
}

/**
 * The placement a loaded run should open on: the operator's own last choice first, then our
 * recommendation, then Amazon's first option. Reopening a run used to snap back to the
 * recommendation, quietly contradicting what they had picked.
 */
export function initialPlacementFor(analysis) {
    if (!analysis) return null;
    const options = analysis.placementOptions || [];
    const exists = (id) => !!id && options.some(o => o.placementOptionId === id);
    if (exists(analysis.selectedPlacementId)) return analysis.selectedPlacementId;
    if (exists(analysis.recommendedPlacementId)) return analysis.recommendedPlacementId;
    return options[0]?.placementOptionId || null;
}
